import { getCurrentLocale } from "Infrastructure/Utils/LocaleUtils";
import { localeIntlUnit } from "Shared/Utils/Format/unit";

export enum DateFormat {
  Date = "Date",
  DateTime = "DateTime",
  DateTimeWithSeconds = "DateTimeWithSeconds",
}

const DATE_FORMAT_OPTIONS: Record<DateFormat, Intl.DateTimeFormatOptions> = {
  [DateFormat.Date]: {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  },
  [DateFormat.DateTime]: {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  },
  [DateFormat.DateTimeWithSeconds]: {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  },
};

/**
 * Formats a date value for display using the current i18n locale.
 * - Date objects and numbers are formatted directly.
 * - Strings with DateFormat.Date use apiStringToDate internally to avoid
 *   UTC offset shifting on date-only strings (e.g. "2024-01-15").
 * - Strings with DateFormat.DateTime/DateTimeWithSeconds use new Date()
 *   to preserve the time component.
 */
export const formatDate = (
  date: string | Date | number | undefined | null,
  dateFormat: DateFormat = DateFormat.Date,
  invalidValue = "",
): string => {
  if (!date) return invalidValue;

  try {
    let d: Date;
    if (typeof date === "string") {
      d =
        dateFormat === DateFormat.Date
          ? (apiStringToDate(date) ?? new Date(date))
          : new Date(date);
    } else {
      d = new Date(date);
    }
    return new Intl.DateTimeFormat(
      getCurrentLocale(),
      DATE_FORMAT_OPTIONS[dateFormat],
    ).format(d);
  } catch {
    return invalidValue;
  }
};

export const formatYearsAndMonths = (
  months: number | undefined,
  emptyPlaceholder?: string,
) => {
  if (months === undefined) {
    return emptyPlaceholder || "";
  }

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  return `${years ? localeIntlUnit(years, getCurrentLocale(), "year") : ""} ${localeIntlUnit(remainingMonths, getCurrentLocale(), "month")}`;
};

/**
 * Converts a JS Date to a "YYYY-MM-DD" string for sending to the BE.
 * Uses local date parts (not UTC) to avoid timezone offset shifting the date.
 * e.g. 2024-01-15 in CET → "2024-01-15" (not "2024-01-14" via toISOString)
 */
export const dateToApiString = (
  date: Date | null | undefined,
): string | null => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Parses a BE date string (e.g. "2024-01-15T00:00:00") to a local midnight Date.
 * Extracts only the date part to avoid UTC offset shifting the date.
 */
export const apiStringToDate = (
  dateString: string | null | undefined,
): Date | null => {
  if (!dateString) return null;
  const [year, month, day] = dateString.substring(0, 10).split("-").map(Number);
  return new Date(year, month - 1, day);
};
